/**
 * Métricas e filtros do Dashboard Comercial (dados reais de Edital + extras).
 */

import {
  chaveGrupoClassificacao,
  filtrarLinhasClassificacao,
  isDesclassificado,
} from '@/lib/classificacaoPregaoUtils';

export const CONCLUSOES_PADRAO = ['Venceu', 'Perdeu', 'Aguardando', 'Cancelado'];

/** Siglas UF (inclui DF). */
export const UFS_BR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA',
  'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

export function parseDataCertame(edital) {
  const raw = edital.data_abertura ?? edital.dataAbertura;
  if (raw == null || raw === '') return null;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^n[ãa]o\s+localizado/i.test(trimmed)) return null;
  if (/^n[ãa]o\s+informado/i.test(trimmed)) return null;
  if (trimmed === 'null' || trimmed === 'undefined') return null;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(parseInt(iso[1], 10), parseInt(iso[2], 10) - 1, parseInt(iso[3], 10));
  const br = raw.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (br) return new Date(parseInt(br[3], 10), parseInt(br[2], 10) - 1, parseInt(br[1], 10));
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

export function getDataRefFiltro(edital) {
  const d = parseDataCertame(edital);
  if (d) return d;
  const c = edital.created_at ?? edital.createdAt;
  return c ? new Date(c) : null;
}

export function parseProbabilidadeFechamento(str) {
  if (str == null || str === '') return null;
  const n = parseInt(String(str).replace(/\D/g, ''), 10);
  return Number.isFinite(n) ? n : null;
}

export function getConclusaoDisputa(edital) {
  const c = edital.conclusao_disputa;
  if (c && CONCLUSOES_PADRAO.includes(c)) return c;
  return 'Aguardando';
}

export function getUfLicitacao(edital) {
  const u = (edital.uf_licitacao ?? '').trim().toUpperCase();
  return u.length === 2 ? u : null;
}

export function isPrimeiroLugar(classificacao) {
  if (classificacao == null || classificacao === '') return false;
  const s = String(classificacao).trim();
  if (s === '1' || s === '1º') return true;
  const n = parseInt(s.replace(/\D/g, ''), 10);
  return n === 1;
}

export function parseColocacaoNumero(classificacao) {
  if (classificacao == null || classificacao === '') return null;
  const n = parseInt(String(classificacao).replace(/\D/g, ''), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Valor total do edital para KPIs: soma valores em classificações ou valor_estimado.
 */
export function getValorTotalKpi(edital) {
  const cls = edital.classificacoes;
  if (Array.isArray(cls) && cls.length > 0) {
    const linhas = filtrarLinhasClassificacao(cls);
    const soma = linhas.reduce((acc, c) => acc + (Number(c.valor_final) || 0), 0);
    if (soma > 0) return soma;
  }
  return Number(edital.valor_estimado ?? edital.valorEstimado) || 0;
}

/** Particiona linhas já normalizadas por `grupo_id` (bucket global agrupado). */
export function particionarClassificacoesPorGrupoKey(linhas) {
  if (!Array.isArray(linhas) || linhas.length === 0) return [];
  const map = new Map();
  for (const c of linhas) {
    const k =
      chaveGrupoClassificacao(c) == null ? '__global__' : String(chaveGrupoClassificacao(c));
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(c);
  }
  return [...map.values()];
}

/** Linhas de classificação (snake_case ou camelCase nos itens); ignora cabeçalhos de grupo. */
export function getClassificacoesEdital(edital) {
  const raw = edital.classificacoes;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const semCabecalhos = filtrarLinhasClassificacao(raw);
  return semCabecalhos.map((c) => {
    const valor = Number(c.valor_final ?? c.valorFinal ?? 0);
    const padrao = c.empresa_padrao ?? c.empresaPadrao;
    const ehPadrao =
      padrao === true ||
      padrao === 1 ||
      padrao === 'true' ||
      padrao === '1';
    return {
      ...c,
      classificacao: c.classificacao,
      valor_final: valor,
      empresa_padrao: ehPadrao,
      desclassificado: isDesclassificado(c),
    };
  });
}

/**
 * Vencedor: colocação 1º; se não houver, menor valor_final > 0 (pregão = menor preço).
 */
export function getLinhaVencedora(classificacoes) {
  if (!Array.isArray(classificacoes) || classificacoes.length === 0) return null;
  const ativas = classificacoes.filter((c) => !isDesclassificado(c));
  if (ativas.length === 0) return null;
  const porColocacao = ativas.find((c) =>
    isPrimeiroLugar(c.classificacao)
  );
  if (porColocacao) return porColocacao;
  const comValor = ativas
    .map((c) => ({
      ...c,
      _v: Number(c.valor_final ?? c.valorFinal ?? 0),
    }))
    .filter((c) => c._v > 0);
  if (comValor.length === 0) return null;
  const minV = Math.min(...comValor.map((c) => c._v));
  return comValor.find((c) => c._v === minV) ?? null;
}

export function getLinhaEmpresaPadrao(classificacoes) {
  if (!Array.isArray(classificacoes)) return null;
  return (
    classificacoes.find((c) => {
      const p = c.empresa_padrao ?? c.empresaPadrao;
      return (
        p === true || p === 1 || p === 'true' || p === '1'
      );
    }) ?? null
  );
}

/**
 * Gap % = ((valor oferta - preço vencedor) / preço vencedor) * 100
 * Empate (valores iguais) => 0. Retorna null se não houver dados.
 */
function gapEntreVencedorENossa(venc, nossa) {
  const precoV = Number(venc.valor_final ?? venc.valorFinal) || 0;
  const nosso = Number(nossa.valor_final ?? nossa.valorFinal) || 0;
  if (precoV <= 0) return null;
  const gapPercent = nosso === precoV ? 0 : ((nosso - precoV) / precoV) * 100;
  return {
    gapPercent,
    precoVencedor: precoV,
    valorOferta: nosso,
  };
}

/**
 * Gap % por grupo (vencedor × empresa padrão no mesmo grupo).
 * Vários grupos com par válido: média aritmética dos gaps.
 */
export function computeGapEdital(edital) {
  const cls = getClassificacoesEdital(edital);
  if (cls.length === 0) return { gapPercent: null, temDados: false };

  const particoes = particionarClassificacoesPorGrupoKey(cls);
  const resultados = [];
  for (const g of particoes) {
    const venc = getLinhaVencedora(g);
    const nossa = getLinhaEmpresaPadrao(g);
    if (!venc || nossa == null) continue;
    const r = gapEntreVencedorENossa(venc, nossa);
    if (r) resultados.push(r);
  }

  if (resultados.length === 0) {
    return { gapPercent: null, temDados: false };
  }

  const gapPercent =
    resultados.reduce((acc, r) => acc + r.gapPercent, 0) / resultados.length;
  const primeiro = resultados[0];
  return {
    gapPercent,
    temDados: true,
    precoVencedor: primeiro.precoVencedor,
    valorOferta: primeiro.valorOferta,
  };
}

/**
 * Colocação numérica só a partir de `edital.classificacoes` (sem usar o campo `classificacao` do edital).
 * Ignora linhas desclassificadas. Mesma prioridade que o dashboard: empresa padrão → menor colocação;
 * senão linha empresa padrão; senão única colocação na tabela. Várias colocações sem critério claro → null.
 */
export function colocacaoNumeroSomenteTabelaClassificacoes(edital) {
  const cls = getClassificacoesEdital(edital).filter((c) => !c.desclassificado);
  if (cls.length === 0) return null;
  const padroes = cls.filter((c) => {
    const p = c.empresa_padrao ?? c.empresaPadrao;
    return p === true || p === 1 || p === 'true' || p === '1';
  });
  if (padroes.length > 0) {
    const numsGrupo = padroes
      .map((c) => parseColocacaoNumero(c.classificacao))
      .filter((n) => n != null);
    if (numsGrupo.length > 0) {
      return Math.min(...numsGrupo);
    }
  }
  const linhaPadrao = getLinhaEmpresaPadrao(cls);
  if (linhaPadrao) {
    const n = parseColocacaoNumero(linhaPadrao.classificacao);
    if (n != null) return n;
  }
  const nums = cls
    .map((c) => parseColocacaoNumero(c.classificacao))
    .filter((n) => n != null);
  if (nums.length === 1) return nums[0];
  return null;
}

/**
 * Colocação exibida no dashboard: da empresa padrão (nossa oferta), nunca média entre
 * todas as linhas de classificação (isso gerava valores como 1,5º quando havia 1º e 2º).
 */
export function mediaColocacaoEdital(edital) {
  const cls = getClassificacoesEdital(edital);
  if (cls.length > 0) {
    const padroes = cls.filter((c) => {
      const p = c.empresa_padrao ?? c.empresaPadrao;
      return p === true || p === 1 || p === 'true' || p === '1';
    });
    if (padroes.length > 0) {
      const numsGrupo = padroes
        .map((c) => parseColocacaoNumero(c.classificacao))
        .filter((n) => n != null);
      if (numsGrupo.length > 0) {
        return Math.min(...numsGrupo);
      }
    }
    const linhaPadrao = getLinhaEmpresaPadrao(cls);
    if (linhaPadrao) {
      const n = parseColocacaoNumero(linhaPadrao.classificacao);
      if (n != null) return n;
    }
    const nums = cls
      .map((c) => parseColocacaoNumero(c.classificacao))
      .filter((n) => n != null);
    if (nums.length === 1) return nums[0];
    const nEdital = parseColocacaoNumero(edital.classificacao);
    if (nEdital != null) return nEdital;
    return null;
  }
  const n = parseColocacaoNumero(edital.classificacao);
  return n != null ? n : null;
}

export function isZonaQuente(edital) {
  const p = parseProbabilidadeFechamento(edital.probabilidade_fechamento);
  const c = getConclusaoDisputa(edital);
  if (p == null || p < 60) return false;
  if (c === 'Venceu' || c === 'Perdeu') return false;
  return true;
}

export function formatCurrencyShort(value) {
  if (value == null || !Number.isFinite(value)) return 'R$ 0';
  const abs = Math.abs(value);
  if (abs >= 1e6) return `R$ ${(value / 1e6).toFixed(1).replace('.', ',')}M`;
  if (abs >= 1e3) return `R$ ${(value / 1e3).toFixed(1).replace('.', ',')}K`;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);
}
